import api from '../../../lib/axios';
import { BaseResponse } from '../../../lib/api-contract';
import { MerchantHeatmapApiResponse, MerchantHeatmapFilters } from './MerchantHeatmap.data';

// Raw API Response Types
export interface DashboardSummaryRawApiResponse {
    active_coupons: {
        count: number;
        description: string;
    };
    overall_conversion_rate: {
        percentage: number;
        formatted: string;
        views: number;
        redemptions: number;
        description: string;
    };
    best_performing: {
        coupon_id: string;
        title: string;
        conversion_rate: number;
        formatted: string;
        redemptions: number;
        description: string;
    };
    redemption_breakdown: {
        in_app: number;
        in_store: number;
        in_app_label: string;
        in_store_label: string;
    };
}

export interface LocationAnalyticsRawApiResponse {
    peak_day: string;
    top_location: string;
    avg_points_spent: number;
    redemption_trends: any[];
    location_performance: {
        total_redemptions: number;
        top_location: {
            location_name: string;
            percentage: number;
        };
        avg_per_location: number;
    };
    user_engagement: {
        avg_points_spent: number;
        top_event: {
            event_name: string;
            attendees_redeemed: number;
        };
    };
}

export interface HeatMapFilter {
    coupon_ids?: string[];
    location_ids?: string[];
    demographics?: {
        min_age?: number;
        max_age?: number;
    };
    start_date?: string;
    end_date?: string;
}

export interface HeatMapLocation {
    // New BE fields
    location_id?: string;
    location_name?: string;
    redemption_count?: number;
    density_level?: 'low' | 'medium' | 'high' | string;
    // Legacy / generic fields
    id?: string;
    name?: string;
    latitude?: number;
    longitude?: number;
    redemptions?: number;
    percentage_of_total?: number;
    top_coupon?: string;
    redemption_rate?: number;
    [key: string]: any;
}

export interface HeatMapRawApiResponse {
    locations: HeatMapLocation[];
    summary: {
        total_redemptions: number;
        areas_covered: number;
        conversion_rate: number;
    };
}

export interface ActivityLevelsRawApiResponse {
    locations: HeatMapLocation[];
}

export interface CouponPerformanceRawApiResponse {
    coupons: Array<{
        coupon_id?: string;
        coupon_name?: string;
        title?: string;
        views?: number;
        redemptions?: number;
        redeemed_in_app?: number;
        usage_in_store?: number;
        conversion_rate?: number | null;
        performance?: string;
        [key: string]: any;
    }>;
}

export interface CouponDropdownOption {
    id: string;
    title?: string;
    name?: string;
    [key: string]: any;
}

export interface CouponDropdownRawApiResponse {
    coupons: CouponDropdownOption[] | null;
}

export interface LocationDropdownOption {
    id: string;
    name: string;
    [key: string]: any;
}

// Fetch APIs
const fetchDashboardSummary = async (): Promise<DashboardSummaryRawApiResponse> => {
    const response = await api.get<BaseResponse<DashboardSummaryRawApiResponse>>('/v1/merchant/coupon-map/dashboard-summary');
    if (!response.data?.data) {
        throw new Error('Invalid API response: missing data');
    }
    return response.data.data;
};

export const fetchLocationAnalytics = async (): Promise<LocationAnalyticsRawApiResponse> => {
    const response = await api.get<BaseResponse<LocationAnalyticsRawApiResponse>>('/v1/merchant/coupon-map/location-analytics');
    if (!response.data?.data) {
        throw new Error('Invalid API response: missing data');
    }
    return response.data.data;
};

const fetchHeatMap = async (filter: HeatMapFilter): Promise<HeatMapRawApiResponse> => {
    const response = await api.post<BaseResponse<HeatMapRawApiResponse>>('/v1/merchant/coupon-map/heat-map', { filter });
    if (!response.data?.data) {
        throw new Error('Invalid API response: missing data');
    }
    return response.data.data;
};

const fetchActivityLevels = async (filter: HeatMapFilter): Promise<ActivityLevelsRawApiResponse> => {
    const response = await api.post<BaseResponse<ActivityLevelsRawApiResponse>>('/v1/merchant/coupon-map/activity-levels', { filter });
    if (!response.data?.data) {
        throw new Error('Invalid API response: missing data');
    }
    return response.data.data;
};

const fetchCouponPerformance = async (filter: HeatMapFilter): Promise<CouponPerformanceRawApiResponse> => {
    const response = await api.post<BaseResponse<CouponPerformanceRawApiResponse>>('/v1/merchant/coupon-map/coupon-performance', { filter });
    if (!response.data?.data) {
        throw new Error('Invalid API response: missing data');
    }
    return response.data.data;
};

const fetchCouponDropdown = async (): Promise<CouponDropdownRawApiResponse> => {
    const response = await api.get<BaseResponse<CouponDropdownRawApiResponse>>('/v1/merchant/local-merchant-coupon/dropdown');
    if (!response.data?.data) {
        throw new Error('Invalid API response: missing data');
    }
    return response.data.data;
};

const fetchLocationDropdown = async (): Promise<LocationDropdownOption[]> => {
    const response = await api.get<BaseResponse<LocationDropdownOption[]>>('/v1/merchant/organization-population/neighborhoods');
    if (!response.data?.data) {
        throw new Error('Invalid API response: missing data');
    }
    return response.data.data;
};

// Mock delay function (for backward compatibility)
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Mock data for development (kept for fallback)
const MOCK_MERCHANT_HEATMAP_DATA: MerchantHeatmapApiResponse = {
    active_coupons: 5,
    active_coupons_limit: 10,
    total_views: 18,
    conversion_rate: 100,
    total_redemptions: 18,
    coupon_performance: [
        {
            coupon_name: '20% Off Summer Special',
            views: 5,
            redeemed_in_app: 18,
            usage_in_store: 18,
            conversion_rate: 100,
            performance: 'excellent',
        },
        {
            coupon_name: 'Coupon Name',
            views: 6,
            redeemed_in_app: 0,
            usage_in_store: 0,
            conversion_rate: null,
            performance: 'good',
        },
        {
            coupon_name: 'Coupon Name',
            views: 6,
            redeemed_in_app: 0,
            usage_in_store: 0,
            conversion_rate: null,
            performance: 'average',
        },
        {
            coupon_name: 'Coupon Name',
            views: 6,
            redeemed_in_app: 0,
            usage_in_store: 0,
            conversion_rate: null,
            performance: 'poor',
        },
        {
            coupon_name: 'Coupon Name',
            views: 6,
            redeemed_in_app: 0,
            usage_in_store: 0,
            conversion_rate: null,
            performance: 'na',
        },
    ],
    best_performing: {
        coupon_name: 'Coupon Name...',
        conversion_rate: 100,
        redemptions: 18,
    },
    redemption_breakdown: {
        in_app: 18,
        in_store: 18,
    },
    locations: [
        {
            id: '1',
            name: 'Eastside District',
            latitude: 33.5670,
            longitude: -84.5810,
            redemptions: 132,
            percentage_of_total: 32,
            top_coupon: 'Free Appetizer',
            redemption_rate: 75, // High
        },
        {
            id: '2',
            name: 'Downtown Fairburn',
            latitude: 33.5671,
            longitude: -84.5811,
            redemptions: 5,
            percentage_of_total: 15.2,
            top_coupon: '20% Off Pizza',
            redemption_rate: 65, // Medium
        },
        {
            id: '3',
            name: 'Westfield Community',
            latitude: 33.5672,
            longitude: -84.5812,
            redemptions: 2,
            percentage_of_total: 18.9,
            top_coupon: 'Buy 1 Get 1',
            redemption_rate: 45, // Low
        },
        {
            id: '4',
            name: 'Southgate Area',
            latitude: 33.5673,
            longitude: -84.5813,
            redemptions: 2,
            percentage_of_total: 18.9,
            top_coupon: '15% Off Lunch',
            redemption_rate: 48, // Low
        },
        {
            id: '5',
            name: 'North Park',
            latitude: 33.5674,
            longitude: -84.5814,
            redemptions: 20,
            percentage_of_total: 18.9,
            top_coupon: 'Free Drink',
            redemption_rate: 35, // Low
        },
    ],
    neighborhood_analysis: [
        {
            area_name: 'Downtown Fairburn',
            total_redemptions: 5,
            percentage_of_total: 15.2,
            top_coupon: '20% Off Pizza...',
            performance: 'excellent',
        },
        {
            area_name: 'Eastside District',
            total_redemptions: 6,
            percentage_of_total: 15.2,
            top_coupon: 'Free Appetizer...',
            performance: 'excellent',
        },
        {
            area_name: 'Westfield Community',
            total_redemptions: 2,
            percentage_of_total: 18.9,
            top_coupon: 'Buy 1 Get 1...',
            performance: 'good',
        },
        {
            area_name: 'Southgate Area',
            total_redemptions: 2,
            percentage_of_total: 18.9,
            top_coupon: '15% Off Lunch...',
            performance: 'good',
        },
        {
            area_name: 'North Park',
            total_redemptions: 20,
            percentage_of_total: 18.9,
            top_coupon: 'Free Drink...',
            performance: 'needs_focus',
        },
    ],
};

// Map filters to API filter format
const mapFiltersToApiFilter = (filters?: MerchantHeatmapFilters): HeatMapFilter => {
    if (!filters) return {};
    
    const apiFilter: HeatMapFilter = {};
    
    // Use coupon_ids from filters (already populated by FiltersSection)
    if (filters.coupon_ids && filters.coupon_ids.length > 0) {
        apiFilter.coupon_ids = filters.coupon_ids;
    }
    
    // Use location_ids from filters (already populated by FiltersSection)
    if (filters.location_ids && filters.location_ids.length > 0) {
        apiFilter.location_ids = filters.location_ids;
    }
    
    if (filters.start_date) {
        apiFilter.start_date = filters.start_date;
    }
    
    if (filters.end_date) {
        apiFilter.end_date = filters.end_date;
    }
    
    // Use demographics_age if available
    if (filters.demographics_age) {
        apiFilter.demographics = {
            min_age: filters.demographics_age.min_age,
            max_age: filters.demographics_age.max_age,
        };
    } else if (filters.demographics && filters.demographics !== 'all') {
        // Parse demographics string to age range
        switch (filters.demographics) {
            case 'ages_18_34':
                apiFilter.demographics = { min_age: 18, max_age: 34 };
                break;
            case 'ages_35_54':
                apiFilter.demographics = { min_age: 35, max_age: 54 };
                break;
            case 'ages_55_plus':
                apiFilter.demographics = { min_age: 55 };
                break;
        }
    }
    
    return apiFilter;
};

// Map API responses to UI format
const mapApiResponsesToUI = (
    summary: DashboardSummaryRawApiResponse | null,
    locationAnalytics: LocationAnalyticsRawApiResponse | null,
    heatMap: HeatMapRawApiResponse | null,
    activityLevels: ActivityLevelsRawApiResponse | null,
    couponPerformance: CouponPerformanceRawApiResponse | null
): MerchantHeatmapApiResponse => {
    // Create a map of activity levels data by location_id for quick lookup
    const activityLevelsMap = new Map<string, any>();
    activityLevels?.locations?.forEach(activityLoc => {
        if (activityLoc.location_id) {
            activityLevelsMap.set(activityLoc.location_id, activityLoc);
        }
    });

    // Map locations from heat-map API and merge with activity-levels data
    const locations =
        heatMap?.locations?.map((loc, index) => {
            // Prefer new BE fields, fall back to generic ones
            const id = loc.id || loc.location_id || loc.location_name || `loc-${index}`;
            const name = loc.name || loc.location_name || 'Unknown Location';

            const redemptions =
                loc.redemptions !== undefined ? loc.redemptions : loc.redemption_count ?? 0;

            // Derive redemption_rate if not provided, using density_level as a proxy
            let redemption_rate = loc.redemption_rate;
            if (redemption_rate === undefined && loc.density_level) {
                if (loc.density_level === 'high') redemption_rate = 80;
                else if (loc.density_level === 'medium') redemption_rate = 60;
                else if (loc.density_level === 'low') redemption_rate = 40;
            }

            // Get activity levels data for this location
            const activityData = activityLevelsMap.get(id);

            return {
                id,
                name,
                latitude: loc.latitude ?? 0,
                longitude: loc.longitude ?? 0,
                redemptions: activityData?.total_redemptions ?? redemptions,
                percentage_of_total: loc.percentage_of_total ?? 0,
                top_coupon: activityData?.top_performing_coupon?.coupon_name || loc.top_coupon || 'N/A',
                redemption_rate: redemption_rate ?? 0,
                growth_percentage: activityData?.growth_percentage,
                demographics: activityData?.demographics?.age_range || undefined,
            };
        }) || [];

    // Map coupon performance
    const coupon_performance = couponPerformance?.coupons?.map((coupon) => ({
        coupon_name: coupon.coupon_name || coupon.title || 'Unknown Coupon',
        views: coupon.views ?? 0,
        redeemed_in_app: coupon.redeemed_in_app ?? 0,
        usage_in_store: coupon.usage_in_store ?? 0,
        conversion_rate: coupon.conversion_rate ?? null,
        performance: (coupon.performance as any) || 'na',
    })) || [];

    // Map neighborhood analysis from location analytics
    const neighborhood_analysis = locationAnalytics?.location_performance ? [{
        area_name: locationAnalytics.location_performance.top_location.location_name || 'Unknown',
        total_redemptions: locationAnalytics.location_performance.total_redemptions,
        percentage_of_total: locationAnalytics.location_performance.top_location.percentage,
        top_coupon: 'N/A', // Not provided by API
        performance: 'excellent' as const, // Default, API doesn't provide this
    }] : [];

    return {
        active_coupons: summary?.active_coupons?.count ?? 0,
        active_coupons_limit: 10, // Not provided by API, using default
        total_views: summary?.overall_conversion_rate?.views ?? 0,
        conversion_rate: summary?.overall_conversion_rate?.percentage ?? 0,
        total_redemptions: summary?.overall_conversion_rate?.redemptions ?? 0,
        coupon_performance,
        best_performing: {
            coupon_name: summary?.best_performing?.title || 'No data available',
            conversion_rate: summary?.best_performing?.conversion_rate ?? 0,
            redemptions: summary?.best_performing?.redemptions ?? 0,
        },
        redemption_breakdown: {
            in_app: summary?.redemption_breakdown?.in_app ?? 0,
            in_store: summary?.redemption_breakdown?.in_store ?? 0,
        },
        locations,
        neighborhood_analysis,
    };
};

export const fetchMerchantHeatmapData = async (filters?: MerchantHeatmapFilters): Promise<MerchantHeatmapApiResponse> => {
    try {
        const apiFilter = mapFiltersToApiFilter(filters);
        
        // Call all APIs in parallel
        const [summary, locationAnalytics, heatMap, activityLevels, couponPerformance] = await Promise.all([
            fetchDashboardSummary().catch((error) => {
                console.error('Failed to fetch dashboard summary:', error);
                return null;
            }),
            fetchLocationAnalytics().catch((error) => {
                console.error('Failed to fetch location analytics:', error);
                return null;
            }),
            fetchHeatMap(apiFilter).catch((error) => {
                console.error('Failed to fetch heat map:', error);
                return null;
            }),
            fetchActivityLevels(apiFilter).catch((error) => {
                console.error('Failed to fetch activity levels:', error);
                return null;
            }),
            fetchCouponPerformance(apiFilter).catch((error) => {
                console.error('Failed to fetch coupon performance:', error);
                return null;
            }),
        ]);
        
        return mapApiResponsesToUI(summary, locationAnalytics, heatMap, activityLevels, couponPerformance);
    } catch (error) {
        console.error('Error fetching merchant heatmap data:', error);
        // Fallback to mock data on error
        return MOCK_MERCHANT_HEATMAP_DATA;
    }
};

// Export dropdown APIs
export const getCouponDropdownOptions = async (): Promise<CouponDropdownOption[]> => {
    try {
        const response = await fetchCouponDropdown();
        return response.coupons || [];
    } catch (error) {
        console.error('Failed to fetch coupon dropdown:', error);
        return [];
    }
};

export const getLocationDropdownOptions = async (): Promise<LocationDropdownOption[]> => {
    try {
        return await fetchLocationDropdown();
    } catch (error) {
        console.error('Failed to fetch location dropdown:', error);
        return [];
    }
};

export const exportMerchantHeatmapCSV = async (filters?: MerchantHeatmapFilters): Promise<Blob> => {
    await delay(500); // Simulate API call delay

    // Fetch data with filters
    const data = await fetchMerchantHeatmapData(filters);

    // Create CSV content with headers
    let csvContent = 'Merchant Heatmap Report\n';
    csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;

    // Location Data Section
    csvContent += 'Location Data\n';
    csvContent += 'Location Name,Latitude,Longitude,Redemptions,% of Total,Top Coupon,Redemption Rate\n';
    data.locations.forEach(location => {
        csvContent += `"${location.name}",${location.latitude},${location.longitude},${location.redemptions},${location.percentage_of_total.toFixed(1)}%,"${location.top_coupon}",${location.redemption_rate.toFixed(1)}%\n`;
    });

    csvContent += '\n';

    // Neighborhood Analysis Section
    csvContent += 'Neighborhood Analysis\n';
    csvContent += 'Area Name,Total Redemptions,% of Total,Top Coupon,Performance\n';
    data.neighborhood_analysis.forEach(neighborhood => {
        csvContent += `"${neighborhood.area_name}",${neighborhood.total_redemptions},${neighborhood.percentage_of_total.toFixed(1)}%,"${neighborhood.top_coupon}",${neighborhood.performance}\n`;
    });

    return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
};

export const exportMerchantHeatmapXLSX = async (filters?: MerchantHeatmapFilters): Promise<Blob> => {
    await delay(500); // Simulate API call delay

    // In real implementation, this would use xlsx library to create Excel file
    // For now, return CSV as placeholder (backend should handle XLSX generation)
    const csvContent = await exportMerchantHeatmapCSV(filters);

    // TODO: Replace with actual XLSX generation when backend API is ready
    // For now, return CSV blob with .xlsx extension
    return csvContent;
};

